import { useState, useEffect, useRef, useCallback } from 'react'
import { getTags, createTag, Tag } from '../api/tags'

interface TagSelectorProps {
  selectedTags: Tag[]
  onChange: (tags: Tag[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

export default function TagSelector({
  selectedTags,
  onChange,
  placeholder = 'Add tags...',
  className = '',
  disabled = false,
}: TagSelectorProps) {
  const [allTags, setAllTags] = useState<Tag[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fetch all tags on mount
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true)
      const tags = await getTags()
      setAllTags(tags.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())))
    } catch (err) {
      console.error('Failed to fetch tags:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter available tags (not already selected)
  const availableTags = allTags.filter(
    tag => !selectedTags.some(selected => selected.id === tag.id)
  )

  // Filter by input value
  const filteredTags = availableTags.filter(tag =>
    tag.name.toLowerCase().includes(inputValue.toLowerCase())
  )

  // Check if we should show "Create new tag" option
  const canCreateNew = inputValue.trim() &&
    !allTags.some(tag => tag.name.toLowerCase() === inputValue.toLowerCase().trim())

  // Add tag to selection
  const handleAddTag = (tag: Tag) => {
    onChange([...selectedTags, tag])
    setInputValue('')
    setIsOpen(false)
    // Do NOT refocus after adding — refocusing triggers onFocus which reopens
    // the dropdown immediately, causing it to intercept subsequent button clicks.
  }

  // Remove tag from selection
  const handleRemoveTag = (tagId: string) => {
    onChange(selectedTags.filter(t => t.id !== tagId))
  }

  // Create new tag and add it
  const handleCreateTag = async () => {
    if (!inputValue.trim() || creating) return

    try {
      setCreating(true)
      const newTag = await createTag({ name: inputValue.trim() })
      setAllTags([...allTags, newTag].sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      ))
      onChange([...selectedTags, newTag])
      setInputValue('')
      setIsOpen(false)
    } catch (err) {
      console.error('Failed to create tag:', err)
    } finally {
      setCreating(false)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (canCreateNew) {
        handleCreateTag()
      } else if (filteredTags.length > 0) {
        handleAddTag(filteredTags[0])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      handleRemoveTag(selectedTags[selectedTags.length - 1].id)
    }
  }

  // Handle input for quick tag creation with delimiter
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    // Check for delimiter (comma, semicolon, or period)
    if (value.endsWith(',') || value.endsWith(';') || value.endsWith('.')) {
      const tagName = value.slice(0, -1).trim()
      if (tagName) {
        const existingTag = allTags.find(t => t.name.toLowerCase() === tagName.toLowerCase())
        if (existingTag && !selectedTags.some(t => t.id === existingTag.id)) {
          handleAddTag(existingTag)
        } else if (!existingTag) {
          // Create new tag with this name
          setInputValue(tagName)
          handleCreateTag()
        }
      }
      setInputValue('')
    } else {
      setInputValue(value)
      if (value) {
        setIsOpen(true)
      }
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected tags and input */}
      <div
        className={`flex flex-wrap items-center gap-2 p-2 border rounded-lg transition-colors
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-text'}
          ${isOpen ? 'border-primary-500 ring-2 ring-primary-100' : 'border-gray-300'}
          ${disabled ? '' : 'hover:border-gray-400'}`}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus()
            setIsOpen(true)
          }
        }}
      >
        {/* Selected tags as chips */}
        {selectedTags.map(tag => (
          <span
            key={tag.id}
            data-testid="tag-chip"
            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
          >
            {tag.name}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveTag(tag.id)
                }}
                className="hover:text-primary-900 focus:outline-none"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedTags.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder-gray-400 disabled:cursor-not-allowed"
        />

        {/* Tag icon */}
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-center text-gray-500 text-sm">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : (
            <>
              {/* Create new tag option */}
              {canCreateNew && (
                <button
                  type="button"
                  onClick={handleCreateTag}
                  disabled={creating}
                  data-testid="tag-create-option"
                  role="option"
                  className="w-full px-3 py-2 text-left text-sm hover:bg-primary-50 flex items-center gap-2 border-b border-gray-100"
                >
                  <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="text-primary-600">
                    {creating ? 'Creating...' : `Create "${inputValue.trim()}"`}
                  </span>
                </button>
              )}

              {/* Tag list */}
              {filteredTags.length > 0 ? (
                filteredTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleAddTag(tag)}
                    role="option"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span className="text-gray-700">{tag.name}</span>
                  </button>
                ))
              ) : !canCreateNew && (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {inputValue ? 'No matching tags' : 'No more tags available'}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
